import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { startTimer, stopTimer } from "../redux/ducks/time";
import { Button, Tooltip } from "@mui/material";
import { PlayArrow, Pause } from "@mui/icons-material";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { showAlert } from "../redux/slices/alerts";   
import { setTimeTracking } from "../redux/slices/ui";
import styled from "@emotion/styled";
import RetainerOverageNotice from "./RetainerOverageNotice";

const TimerButtonStyled = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(1),
}));

const StopTimerDialog = ({ stopTimerDialogOpen, setStopTimerDialogOpen, handleConfirmStopTimer, timerSummary, setTimerSummary, retainerStatus, overageOverride, setOverageOverride }) => {
    const isOverageBlocked = retainerStatus?.level === "block" && !overageOverride;
    return (
        <Dialog open={stopTimerDialogOpen} onClose={() => setStopTimerDialogOpen(false)}>
            <DialogTitle>Stop Timer</DialogTitle>
            <DialogContent>
                <RetainerOverageNotice status={retainerStatus} requireOverride override={overageOverride} onOverrideChange={setOverageOverride} />
                <TextField
                    autoFocus
                    margin="dense"
                    label="Summary"
                    type="text"
                    fullWidth
                    value={timerSummary}
                    onChange={(e) => setTimerSummary(e.target.value)}
                    placeholder="What did you work on?"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setStopTimerDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleConfirmStopTimer} variant="contained" disabled={isOverageBlocked}>
                    Stop Timer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TimerButton = ({ parentId, parentType, runningTimerId = null, onStartTimer = null, onStopTimer = null, retainerStatus = null }) => {
    const dispatch = useDispatch();
    const [isRunning, setIsRunning] = useState(runningTimerId ? true : false);
    const [canStartTimer, setCanStartTimer] = useState(true);
    const [timerId, setTimerId] = useState(runningTimerId);
    const timeTracking = useSelector((state) => state.ui.timeTracking);
    const [timerSummary, setTimerSummary] = useState("");
    const [stopTimerDialogOpen, setStopTimerDialogOpen] = useState(false);
    const [overageOverride, setOverageOverride] = useState(false);
    const isOverageBlocked = retainerStatus?.level === "block" && !overageOverride;

    useEffect(() => {
        if (stopTimerDialogOpen) {
            setOverageOverride(false);
        }
    }, [stopTimerDialogOpen]);


    useEffect(() => {
        setIsRunning(timeTracking && runningTimerId ? true : false);
        setCanStartTimer((timeTracking && runningTimerId ? true : false ) || !timeTracking);
        setTimerId(runningTimerId);
    }, [runningTimerId, timeTracking]);

    const handleStartTimer = () => {
        dispatch(
            startTimer(parentType, parentId, {}, (data) => {
                const startedTimer = data.timeRecord; 
                setTimerId(startedTimer._id);

                if (onStartTimer) {
                    onStartTimer(startedTimer);
                }
                dispatch(setTimeTracking(true));
            })
        );
        setIsRunning(true);
    };

    const handleStopTimer = () => {
        setStopTimerDialogOpen(true);
    };

    const handleConfirmStopTimer = () => {
        if (isOverageBlocked) return;
        if (timerId) {
            dispatch(
                stopTimer(timerId, { summary: timerSummary }, (response) => {
                    if (response && response.timeRecord) {
                        setStopTimerDialogOpen(false);
                        setTimerSummary("");
                        
                        if (onStopTimer) {
                            onStopTimer(response.timeRecord);
                        }
                        dispatch(setTimeTracking(false));
                    } else {
                        dispatch(showAlert({ severity: "error", message: response.message }));
                    }
                })
            );
        }
    };

    return (
        <>
            <Tooltip title={isRunning ? "Stop Timer" : "Start Timer"}>
                <TimerButtonStyled variant="contained" size="small" onClick={isRunning ? handleStopTimer : handleStartTimer} color={isRunning ? "error" : "primary"} disabled={!canStartTimer && !isRunning}>
                    {isRunning ? <Pause /> : <PlayArrow />}
                </TimerButtonStyled>
            </Tooltip>
            <StopTimerDialog
                stopTimerDialogOpen={stopTimerDialogOpen}
                setStopTimerDialogOpen={setStopTimerDialogOpen}
                handleConfirmStopTimer={handleConfirmStopTimer}
                timerSummary={timerSummary}
                setTimerSummary={setTimerSummary}
                retainerStatus={retainerStatus}
                overageOverride={overageOverride}
                setOverageOverride={setOverageOverride}
            />
        </>
    );
};

export default TimerButton;